import React, {
  useEffect,
  useState,
} from "react";

function safeArray(value) {
  return Array.isArray(value)
    ? value
    : [];
}

function clean(value) {
  return String(value ?? "").trim();
}

function issueCounts(issues) {
  return safeArray(issues).reduce(
    (result, item) => {
      if (
        item?.severity ===
        "blocking"
      ) {
        result.blocking += 1;
      } else if (
        item?.severity ===
        "warning"
      ) {
        result.warning += 1;
      }

      return result;
    },
    {
      blocking: 0,
      warning: 0,
    }
  );
}

export default function CreativePublicationProofViewer({
  lang = "en",
  title = "",
  format = {},
  cover = {},
  interiorItems = [],
  issues = [],
  renderCover,
  renderInteriorItem,
  formatIssue,
  renderPreflightReport,
  onReturn,
  onExit,
  returnLabel = "",
  bookPageGeometry = false,
  openBookMode = false,
  coverContentGeometry = false,
  directInteriorNavigation = false,
  stickyInteriorNavigation = false,
}) {
  const isFR =
    String(lang).toLowerCase() ===
    "fr";

  const items =
    safeArray(interiorItems);

  const proofIssues =
    safeArray(issues);

  const [activeTab, setActiveTab] =
    useState("cover");

  const [
    activeInteriorIndex,
    setActiveInteriorIndex,
  ] = useState(0);

  useEffect(() => {
    setActiveInteriorIndex(
      (current) =>
        Math.min(
          Math.max(
            current,
            0
          ),
          Math.max(
            items.length - 1,
            0
          )
        )
    );
  }, [items.length]);

  const counts =
    issueCounts(proofIssues);

  const trim =
    format?.trim || {};

  const measurable =
    Boolean(
      trim.measurable &&
      Number(trim.width) > 0 &&
      Number(trim.height) > 0
    );

  const aspectRatio =
    measurable
      ? `${trim.width} / ${trim.height}`
      : "4 / 5";

  const bleedEnabled =
    clean(format?.bleed) ===
    "yes";

  const activeInteriorItem =
    items[
      activeInteriorIndex
    ] || null;

  const activeInteriorPageSpan =
    openBookMode
      ? 2
      : Number(
          activeInteriorItem?.pageSpan
        ) === 2
        ? 2
        : 1;

  const activeInteriorAspectRatio =
    measurable
      ? `${
          Number(trim.width) *
          activeInteriorPageSpan
        } / ${Number(trim.height)}`
      : activeInteriorPageSpan === 2
        ? "8 / 5"
        : "4 / 5";

  const contentDrivenCover =
    activeTab === "cover" &&
    coverContentGeometry;

  const proofFrameAspectRatio =
    contentDrivenCover
      ? undefined
      : activeTab === "interior" &&
        bookPageGeometry
        ? activeInteriorAspectRatio
        : aspectRatio;

  const proofFrameMaxWidth =
    contentDrivenCover
      ? 1180
      : activeTab === "interior" &&
        bookPageGeometry
        ? activeInteriorPageSpan === 2
          ? 980
          : 640
        : 980;

  const activeInteriorPageLabel =
    clean(
      activeInteriorItem
        ?.pageLabel
    );

  const canGoPrevious =
    activeInteriorIndex > 0;

  const canGoNext =
    activeInteriorIndex <
    items.length - 1;

  const trimLabel =
    trim.label ||
    (
      isFR
        ? "Format non indiqué"
        : "Trim size not provided"
    );

  const issueText = (item) => {
    if (
      typeof formatIssue ===
      "function"
    ) {
      return formatIssue(
        item,
        isFR
      );
    }

    return clean(item?.code)
      .replaceAll("_", " ");
  };

  return (
    <section
      aria-label={
        isFR
          ? "Épreuve de publication"
          : "Publication proof"
      }
    >
      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems:
            "flex-start",
          gap: 16,
          flexWrap: "wrap",
          marginBottom: 18,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 900,
              letterSpacing:
                "0.1em",
              textTransform:
                "uppercase",
              color: "#b45309",
            }}
          >
            {isFR
              ? "Épreuve privée"
              : "Private Proof"}
          </div>

          <h2
            className="ns-section-title"
            style={{
              margin:
                "6px 0 7px",
            }}
          >
            {title ||
              (
                isFR
                  ? "Épreuve de publication"
                  : "Publication Proof"
              )}
          </h2>

          <p
            className="ns-card-meta"
            style={{
              margin: 0,
              maxWidth: 760,
              lineHeight: 1.55,
            }}
          >
            {isFR
              ? "Vérifiez la couverture et l’intérieur dans un cadre de mise en page. Cette vue ne publie et n’exporte rien."
              : "Review the cover and interior inside a layout frame. This view does not publish or export anything."}
          </p>
        </div>

        {typeof onExit ===
        "function" ? (
          <button
            className="ns-button ns-button-outline"
            type="button"
            onClick={onExit}
          >
            {isFR
              ? "Retour à Mes projets"
              : "Return to My Projects"}
          </button>
        ) : null}
      </div>

      <div
        className="ns-card"
        style={{
          padding: 16,
          borderRadius: 16,
          marginBottom: 18,
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 12,
        }}
      >
        <div
          className="ns-nbb-inner-panel"
          style={{
            padding: 13,
            borderRadius: 12,
            background: "#f8fafc",
          }}
        >
          <div
            style={{
              fontSize: 12,
              color: "#64748b",
              marginBottom: 4,
            }}
          >
            {isFR
              ? "Format"
              : "Trim size"}
          </div>

          <strong>
            {trimLabel}
          </strong>
        </div>

        <div
          className="ns-nbb-inner-panel"
          style={{
            padding: 13,
            borderRadius: 12,
            background: "#f8fafc",
          }}
        >
          <div
            style={{
              fontSize: 12,
              color: "#64748b",
              marginBottom: 4,
            }}
          >
            {isFR
              ? "Fond perdu"
              : "Bleed"}
          </div>

          <strong>
            {bleedEnabled
              ? (
                  isFR
                    ? "Prévu"
                    : "Planned"
                )
              : (
                  isFR
                    ? "Non prévu"
                    : "Not planned"
                )}
          </strong>
        </div>

        <div
          className="ns-nbb-inner-panel"
          style={{
            padding: 13,
            borderRadius: 12,
            background:
              counts.blocking > 0
                ? "#fef2f2"
                : "#ecfdf5",
          }}
        >
          <div
            style={{
              fontSize: 12,
              color: "#64748b",
              marginBottom: 4,
            }}
          >
            {isFR
              ? "Problèmes bloquants"
              : "Blocking issues"}
          </div>

          <strong>
            {counts.blocking}
          </strong>
        </div>

        <div
          className="ns-nbb-inner-panel"
          style={{
            padding: 13,
            borderRadius: 12,
            background:
              counts.warning > 0
                ? "#fff7ed"
                : "#f8fafc",
          }}
        >
          <div
            style={{
              fontSize: 12,
              color: "#64748b",
              marginBottom: 4,
            }}
          >
            {isFR
              ? "Avertissements"
              : "Warnings"}
          </div>

          <strong>
            {counts.warning}
          </strong>
        </div>
      </div>

      {proofIssues.length > 0 ? (
        typeof renderPreflightReport === "function" ? (
          renderPreflightReport({
            issues: proofIssues,
            counts,
            issueText,
            isFR,
          })
        ) : (
        <section
          className="ns-card"
          aria-label={
            isFR
              ? "Rapport de contrôle"
              : "Preflight report"
          }
          style={{
            padding: 16,
            borderRadius: 16,
            marginBottom: 18,
          }}
        >
          <h3
            className="ns-card-title"
            style={{
              margin: "0 0 12px",
            }}
          >
            {isFR
              ? "Rapport de contrôle"
              : "Preflight Report"}
          </h3>

          <div
            style={{
              display: "grid",
              gap: 9,
            }}
          >
            {proofIssues.map(
              (item, index) => (
                <div
                  key={
                    `${item?.code || "issue"}-${item?.targetId || index}`
                  }
                  role={
                    item?.severity ===
                    "blocking"
                      ? "alert"
                      : undefined
                  }
                  className="ns-nbb-inner-panel"
                  style={{
                    padding: 12,
                    borderRadius: 11,
                    background:
                      item?.severity ===
                      "blocking"
                        ? "#fef2f2"
                        : "#fff7ed",
                    border:
                      item?.severity ===
                      "blocking"
                        ? "1px solid rgba(185,28,28,0.24)"
                        : "1px solid rgba(194,65,12,0.22)",
                  }}
                >
                  {issueText(item)}
                </div>
              )
            )}
          </div>
        </section>
        )
      ) : null}

      <div
        role="tablist"
        aria-label={
          isFR
            ? "Parties de l’épreuve"
            : "Proof sections"
        }
        className="ns-card"
        style={{
          padding: 8,
          borderRadius: 15,
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          marginBottom: 16,
        }}
      >
        <button
          role="tab"
          aria-selected={
            activeTab === "cover"
          }
          className={
            activeTab === "cover"
              ? "ns-button ns-button-primary"
              : "ns-button ns-button-outline"
          }
          type="button"
          onClick={() =>
            setActiveTab("cover")
          }
        >
          {isFR
            ? "Épreuve de couverture"
            : "Cover Proof"}
        </button>

        <button
          role="tab"
          aria-selected={
            activeTab ===
            "interior"
          }
          className={
            activeTab ===
            "interior"
              ? "ns-button ns-button-primary"
              : "ns-button ns-button-outline"
          }
          type="button"
          onClick={() =>
            setActiveTab(
              "interior"
            )
          }
        >
          {isFR
            ? "Épreuve intérieure"
            : "Interior Proof"}
        </button>
      </div>

      {activeTab ===
      "interior" ? (
        <div
          className="ns-card"
          data-creative-publication-interior-navigator="true"
          style={{
            padding: 12,
            borderRadius: 15,
            marginBottom: 16,
            display: "flex",
            justifyContent:
              "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            position:
              stickyInteriorNavigation
                ? "sticky"
                : undefined,
            top:
              stickyInteriorNavigation
                ? 118
                : undefined,
            zIndex:
              stickyInteriorNavigation
                ? 30
                : undefined,
            background:
              stickyInteriorNavigation
                ? "#ffffff"
                : undefined,
            boxShadow:
              stickyInteriorNavigation
                ? "0 8px 24px rgba(15,23,42,0.10)"
                : undefined,
          }}
        >
          <button
            className="ns-button ns-button-outline"
            type="button"
            disabled={
              !canGoPrevious
            }
            onClick={() =>
              setActiveInteriorIndex(
                (current) =>
                  Math.max(
                    0,
                    current - 1
                  )
              )
            }
          >
            {isFR
              ? "Précédent"
              : "Previous"}
          </button>

          {directInteriorNavigation &&
          items.length > 0 ? (
            <label
              data-creative-publication-interior-jump="true"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                minWidth:
                  "min(620px, 58vw)",
                flex:
                  "1 1 420px",
                color: "#334155",
                fontSize: 13,
                fontWeight: 800,
              }}
            >
              <span
                style={{
                  whiteSpace:
                    "nowrap",
                }}
              >
                {isFR
                  ? "Aller à"
                  : "Jump to"}
              </span>

              <select
                aria-label={
                  isFR
                    ? "Aller directement à un élément du livre"
                    : "Jump directly to a book item"
                }
                value={
                  activeInteriorIndex
                }
                onChange={(event) =>
                  setActiveInteriorIndex(
                    Math.max(
                      0,
                      Math.min(
                        items.length - 1,
                        Number(
                          event.target.value
                        ) || 0
                      )
                    )
                  )
                }
                style={{
                  width: "100%",
                  minWidth: 0,
                  minHeight: 38,
                  padding:
                    "7px 34px 7px 11px",
                  borderRadius: 9,
                  border:
                    "1px solid #cbd5e1",
                  background:
                    "#ffffff",
                  color:
                    "#0f172a",
                  font:
                    "inherit",
                  fontWeight: 800,
                  cursor:
                    "pointer",
                }}
              >
                {items.map(
                  (item, index) => (
                    <option
                      key={
                        clean(
                          item?.itemId
                        ) ||
                        `proof-item-${index + 1}`
                      }
                      value={index}
                    >
                      {clean(
                        item?.pageLabel
                      ) ||
                        (
                          isFR
                            ? `Élément ${index + 1} sur ${items.length}`
                            : `Item ${index + 1} of ${items.length}`
                        )}
                    </option>
                  )
                )}
              </select>
            </label>
          ) : (
            <strong
              aria-live="polite"
              style={{
                color: "#334155",
              }}
            >
              {items.length > 0
                ? (
                    activeInteriorPageLabel ||
                    (
                      isFR
                        ? `Élément ${activeInteriorIndex + 1} sur ${items.length}`
                        : `Item ${activeInteriorIndex + 1} of ${items.length}`
                    )
                  )
                : (
                    isFR
                      ? "Aucun élément intérieur"
                      : "No interior items"
                  )}
            </strong>
          )}

          <button
            className="ns-button ns-button-primary"
            type="button"
            disabled={!canGoNext}
            onClick={() =>
              setActiveInteriorIndex(
                (current) =>
                  Math.min(
                    items.length - 1,
                    current + 1
                  )
              )
            }
          >
            {isFR
              ? "Suivant"
              : "Next"}
          </button>
        </div>
      ) : null}

      {activeTab ===
      "interior" &&
      bookPageGeometry &&
      !openBookMode &&
      activeInteriorItem ? (
        <div
          data-testid="creative-publication-book-page-meta"
          style={{
            maxWidth:
              proofFrameMaxWidth,
            margin:
              "0 auto 10px",
            display: "flex",
            justifyContent:
              "space-between",
            alignItems:
              "center",
            gap: 12,
            flexWrap:
              "wrap",
            color:
              "#475569",
            fontSize: 13,
          }}
        >
          <span
            style={{
              fontWeight: 800,
              letterSpacing:
                "0.04em",
              textTransform:
                "uppercase",
            }}
          >
            {activeInteriorPageSpan ===
            2
              ? (
                  isFR
                    ? "Double page"
                    : "Double-page spread"
                )
              : (
                  isFR
                    ? "Page simple"
                    : "Single page"
                )}
          </span>

          <strong
            style={{
              color:
                activeInteriorItem
                  ?.oversized
                  ? "#9a3412"
                  : activeInteriorItem
                      ?.ready
                    ? "#166534"
                    : "#991b1b",
            }}
          >
            {activeInteriorItem
              ?.oversized
              ? (
                  isFR
                    ? "À diviser"
                    : "Needs division"
                )
              : activeInteriorItem
                  ?.ready
                ? (
                    isFR
                      ? "Contenu prêt"
                      : "Content ready"
                  )
                : (
                    isFR
                      ? "À corriger"
                      : "Needs correction"
                  )}
          </strong>
        </div>
      ) : null}

      {activeTab ===
      "interior" &&
      bookPageGeometry &&
      openBookMode &&
      activeInteriorItem ? (
        <div
          data-testid="creative-publication-open-book-status"
          style={{
            maxWidth:
              proofFrameMaxWidth,
            margin:
              "0 auto 10px",
            display: "flex",
            justifyContent:
              "flex-end",
            alignItems:
              "center",
            minHeight: 22,
          }}
        >
          <strong
            style={{
              color:
                activeInteriorItem
                  ?.oversized
                  ? "#9a3412"
                  : activeInteriorItem
                      ?.ready
                    ? "#166534"
                    : "#991b1b",
            }}
          >
            {activeInteriorItem
              ?.oversized
              ? (
                  isFR
                    ? "À diviser"
                    : "Needs division"
                )
              : activeInteriorItem
                  ?.ready
                ? (
                    isFR
                      ? "Contenu prêt"
                      : "Content ready"
                  )
                : (
                    isFR
                      ? "À corriger"
                      : "Needs correction"
                  )}
          </strong>
        </div>
      ) : null}

      <div
        data-testid="creative-publication-proof-frame-shell"
        style={{
          maxWidth:
            proofFrameMaxWidth,
          margin: "0 auto",
          padding:
            bleedEnabled
              ? 18
              : 0,
          border:
            bleedEnabled
              ? "2px dashed #f97316"
              : "none",
          borderRadius: 18,
          background:
            bleedEnabled
              ? "#fff7ed"
              : "transparent",
        }}
      >
        <div
          data-testid="creative-publication-proof-frame"
          className="ns-card ns-nbb-major-panel"
          style={{
            width: "100%",
            aspectRatio:
              proofFrameAspectRatio,
            minHeight:
              contentDrivenCover ||
              (
                activeTab ===
                  "interior" &&
                bookPageGeometry
              )
                ? 0
                : 420,
            padding:
              "clamp(18px, 3vw, 32px)",
            boxSizing:
              "border-box",
            borderRadius: 16,
            border:
              "2px solid #64748b",
            background: "#ffffff",
            overflow:
              contentDrivenCover
                ? "visible"
                : activeTab ===
                    "interior" &&
                  bookPageGeometry
                  ? "hidden"
                  : "auto",
            display:
              activeTab ===
                "interior" &&
              bookPageGeometry
                ? "flex"
                : "block",
            flexDirection:
              activeTab ===
                "interior" &&
              bookPageGeometry
                ? "column"
                : undefined,
          }}
        >
          {activeTab ===
          "cover" ? (
            typeof renderCover ===
            "function" ? (
              renderCover(cover)
            ) : (
              <div>
                {isFR
                  ? "Aucune épreuve de couverture disponible."
                  : "No cover proof is available."}
              </div>
            )
          ) : activeInteriorItem &&
            typeof renderInteriorItem ===
              "function" ? (
            renderInteriorItem(
              activeInteriorItem,
              activeInteriorIndex
            )
          ) : (
            <div>
              {isFR
                ? "Aucun élément intérieur disponible."
                : "No interior item is available."}
            </div>
          )}
        </div>
      </div>

      {typeof onReturn ===
      "function" ? (
        <div
          style={{
            marginTop: 18,
          }}
        >
          <button
            className="ns-button ns-button-outline"
            type="button"
            onClick={onReturn}
          >
            {returnLabel ||
              (
                isFR
                  ? "Retour"
                  : "Return"
              )}
          </button>
        </div>
      ) : null}
    </section>
  );
}
